import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { AppLink } from '@/shared/components/AppLink';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/widgets/dropdown-menu';
import {
  HEADER_CTA_CLICKED,
  headerCtaClickedEventProperties,
} from '@/shared/features/analytics/utils/headerCtaClicked';
import { useTrackHeaderCtaClick } from '@/shared/features/header/presentation/hooks/useTrackHeaderCtaClick';

interface AccountDropdownProps {
  userName?: string;
  /** Compact icon + “Profile” label for desktop header utilities. */
  presentation?: 'default' | 'headerProfile';
  accentColor?: string;
  isLoggedIn?: boolean;
  onOpenLogin?: (mode?: 'login' | 'signup') => void;
}

const ACCOUNT_MENU_ITEMS: Array<{ label: string; href: string }> = [
  { label: 'Video Call History', href: '/account/video-call-tah-history' },
  { label: 'Order History', href: '/account/orderhistory' },
  { label: 'My Gold Mine 10+1 Plan', href: '/goldmine.html' },
  { label: 'My Gold Reserve', href: '/account/goldreservehistory' },
  { label: 'Blue Cash', href: '/account/bscash' },
  { label: 'Blue Credits', href: '/account/bscredit' },
  { label: 'Blue Vouchers', href: '/account/bluevouchers' },
  { label: 'True Blue', href: '/account/trueblue' },
  { label: 'Shipping Address', href: '/account/address' },
  { label: 'Personal Information', href: '/account/identity' },
  { label: 'Sign Out', href: '/signout' },
];

/**
 * Caret under Profile: `align="end"` matches panel right edge to trigger right edge;
 * offset from right by half trigger width (52px / 58px at lg) so the tip sits on the icon center.
 * (Center-align + collision shift was moving the panel while the caret stayed at 50% width.)
 */
/** Tip ~center of profile column after utilities went to variable width + horizontal padding. */
const CARET_HEADER_PROFILE =
  "before:pointer-events-none before:absolute before:-top-2 before:right-[26px] before:z-10 before:h-0 before:w-0 before:-translate-x-1/2 before:border-b-[8px] before:border-l-[8px] before:border-r-[8px] before:border-b-gray-200 before:border-l-transparent before:border-r-transparent before:content-[''] min-[1200px]:before:right-[20px] after:pointer-events-none after:absolute after:-top-[7px] after:right-[26px] after:z-10 after:h-0 after:w-0 after:-translate-x-1/2 after:border-b-[7px] after:border-l-[7px] after:border-r-[7px] after:border-b-white after:border-l-transparent after:border-r-transparent after:content-[''] min-[1200px]:after:right-[22px]";

const CARET_END =
  "before:pointer-events-none before:absolute before:-top-2 before:right-6 before:z-10 before:h-0 before:w-0 before:border-b-[8px] before:border-l-[8px] before:border-r-[8px] before:border-b-gray-200 before:border-l-transparent before:border-r-transparent before:content-[''] after:pointer-events-none after:absolute after:-top-[7px] after:right-[25px] after:z-10 after:h-0 after:w-0 after:border-b-[7px] after:border-l-[7px] after:border-r-[7px] after:border-b-white after:border-l-transparent after:border-r-transparent after:content-['']";

export default function AccountDropdown({
  userName,
  presentation = 'default',
  accentColor = '#001d3d',
  isLoggedIn,
  onOpenLogin,
}: AccountDropdownProps) {
  const [open, setOpen] = useState(false);
  const closeTimeoutRef = useRef<number | null>(null);
  const isHeaderProfile = presentation === 'headerProfile';
  const showGuestMenu = isHeaderProfile && isLoggedIn === false;
  const trackHeaderCtaClick = useTrackHeaderCtaClick();

  useEffect(() => {
    const root = document.documentElement;
    if (open) root.setAttribute('data-account-menu-open', 'true');
    else root.removeAttribute('data-account-menu-open');
    return () => {
      root.removeAttribute('data-account-menu-open');
    };
  }, [open]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current !== null) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const openMenu = () => {
    clearCloseTimeout();
    setOpen(true);
  };

  const scheduleClose = () => {
    clearCloseTimeout();
    // Small delay keeps hover transition smooth between trigger and menu.
    closeTimeoutRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimeoutRef.current = null;
    }, 100);
  };

  const contentShellClass =
    'relative w-auto min-w-[220px] overflow-visible rounded-xl border border-gray-200 bg-white p-0 shadow-lg ' +
    (isHeaderProfile ? CARET_HEADER_PROFILE : CARET_END);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={
            isHeaderProfile
              ? 'flex shrink-0 flex-col items-center gap-1.5 px-2 transition-opacity hover:opacity-75 min-[1100px]:px-2.5 min-[1200px]:px-3'
              : 'flex items-center gap-1 text-gray-700 transition-colors hover:text-hover'
          }
          style={isHeaderProfile ? { color: accentColor } : undefined}
          onMouseEnter={openMenu}
          onMouseLeave={scheduleClose}
          onClick={() => {
            if (isHeaderProfile) trackHeaderCtaClick('Profile');
          }}
        >
          {isHeaderProfile ? (
            <>
              <svg aria-hidden viewBox="0 0 24 24" className="h-6 w-6 shrink-0" fill="none" style={{ color: accentColor }}>
                <path d="M11.9847 15.3462C8.11707 15.3462 4.81421 15.931 4.81421 18.2729C4.81421 20.6148 8.09611 21.2205 11.9847 21.2205C15.8523 21.2205 19.1542 20.6348 19.1542 18.2938C19.1542 15.9529 15.8733 15.3462 11.9847 15.3462Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M11.9846 12.0059C14.5227 12.0059 16.5799 9.94781 16.5799 7.40971C16.5799 4.87162 14.5227 2.81448 11.9846 2.81448C9.44655 2.81448 7.38845 4.87162 7.38845 7.40971C7.37988 9.93924 9.42369 11.9973 11.9523 12.0059H11.9846Z" stroke="currentColor" strokeWidth="1.42857" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="whitespace-nowrap text-center text-[10px] font-semibold leading-none sm:text-[11px]">
                Profile
              </span>
            </>
          ) : (
            <>
              <span>Hi{userName ? `, ${userName}` : ''}</span>
              <ChevronDown className="h-3.5 w-3.5" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="bottom"
        sideOffset={8}
        avoidCollisions={!isHeaderProfile}
        className={contentShellClass}
        onMouseEnter={openMenu}
        onMouseLeave={() => setOpen(false)}
      >
        {showGuestMenu ? (
          <>
            <DropdownMenuItem asChild className="cursor-pointer rounded-none px-0 py-0 focus:bg-transparent">
              {onOpenLogin ? (
                <button
                  type="button"
                  className="block w-full rounded-t-xl px-4 py-2.5 text-left text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-hover"
                  onClick={() => {
                    trackHeaderCtaClick('Login');
                    setOpen(false);
                    onOpenLogin('login');
                  }}
                >
                  Login
                </button>
              ) : (
                <AppLink
                  to="/showLoginPage"
                  shouldRefresh
                  eventName={HEADER_CTA_CLICKED}
                  eventProperties={headerCtaClickedEventProperties('Login')}
                  className="block w-full rounded-t-xl px-4 py-2.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-hover"
                  onClick={() => setOpen(false)}
                >
                  Login
                </AppLink>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator className="m-0 h-px bg-gray-200" />
            <DropdownMenuItem asChild className="cursor-pointer rounded-none px-0 py-0 focus:bg-transparent">
              {onOpenLogin ? (
                <button
                  type="button"
                  className="block w-full rounded-b-xl px-4 py-2.5 text-left text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-hover"
                  onClick={() => {
                    trackHeaderCtaClick('Signup');
                    setOpen(false);
                    onOpenLogin('signup');
                  }}
                >
                  Signup
                </button>
              ) : (
                <AppLink
                  to="/showLoginPage"
                  shouldRefresh
                  eventName={HEADER_CTA_CLICKED}
                  eventProperties={headerCtaClickedEventProperties('Signup')}
                  className="block w-full rounded-b-xl px-4 py-2.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-hover"
                  onClick={() => setOpen(false)}
                >
                  Signup
                </AppLink>
              )}
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuLabel className="rounded-t-xl bg-gray-50 px-4 py-2.5 text-sm font-semibold text-gray-800">
              {userName || 'Account'}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="m-0 h-px bg-gray-200" />

            {ACCOUNT_MENU_ITEMS.map((item, idx) => {
              const isLast = idx === ACCOUNT_MENU_ITEMS.length - 1;
              return (
                <div key={item.href}>
                  <DropdownMenuItem asChild className="cursor-pointer rounded-none px-0 py-0 focus:bg-transparent">
                    <AppLink
                      to={item.href}
                      shouldRefresh
                      eventName={isHeaderProfile ? HEADER_CTA_CLICKED : undefined}
                      eventProperties={
                        isHeaderProfile ? headerCtaClickedEventProperties(item.label) : undefined
                      }
                      className={`block w-full cursor-pointer whitespace-nowrap px-4 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-hover ${isLast ? 'rounded-b-xl' : ''}`}
                    >
                      {item.label}
                    </AppLink>
                  </DropdownMenuItem>
                  {!isLast ? <DropdownMenuSeparator className="m-0 h-px bg-gray-200" /> : null}
                </div>
              );
            })}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
